import React from "react";
import { useHistory } from "react-router-dom";
import { Avatar, Box, Button, Chip, IconButton, Skeleton, Tooltip, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import OpsPanel from "../OpsPanel";
import { Token } from "../../theme";
import { Routes } from "../../types/Routes";
import { useWorklogsApproval } from "../../hooks/useWorklogsApproval";
import PendingUserApproval from "../../models/PendingUserApproval";
import OpsDateUtils from "../../utils/OpsDateUtils";
import OpsStringUtils from "../../utils/OpsStringUtils";
import { worklogApprovalKey } from "../../utils/WorklogApprovalUtils";
import WorklogReportUtils from "../../utils/WorklogReportUtils";

interface WorklogsApprovalTileProps {
  onApprovalChange?: () => void;
}

const iconButtonBase = {
  width: 28,
  height: 28,
  borderRadius: Token.radiusControl,
  "&.Mui-disabled": {
    backgroundColor: Token.surfaceActive,
    color: Token.textTertiary,
  },
};

const WorklogsApprovalSkeleton: React.FC = () => (
  <Box style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    {Array.from({ length: 4 }).map((_, index) => (
      <Box
        key={`approval-skeleton-${index}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 4px",
        }}
      >
        <Skeleton variant="circular" width={32} height={32} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="58%" height={16} />
          <Skeleton variant="text" width="42%" height={14} />
        </Box>
        <Skeleton variant="text" width={36} height={16} />
        <Skeleton variant="rounded" width={28} height={28} />
        <Skeleton variant="rounded" width={28} height={28} />
      </Box>
    ))}
  </Box>
);

const toWeekDate = (user: PendingUserApproval): Date => OpsDateUtils.parseIsoDate(user.weekFirstDayIso);

export const WorklogsApprovalTile: React.FC<WorklogsApprovalTileProps> = ({ onApprovalChange }) => {
  const history = useHistory();
  const { pendingUsers, isLoading, isApprovingAll, processingId, handleApprove, handleReject, handleApproveAll } =
    useWorklogsApproval(onApprovalChange);

  const openUserWorklog = (user: PendingUserApproval) => {
    const weekDate = toWeekDate(user);
    history.push(WorklogReportUtils.getUrlToUserWorklog(user.userExternalId, user.assignationExternalId, weekDate, weekDate.getDate()));
  };

  const openProjectReport = (event: React.MouseEvent, user: PendingUserApproval) => {
    event.stopPropagation();
    const weekDate = toWeekDate(user);
    history.push(`${Routes.WORKLOG_REPORT}?project=${user.projectExternalId}&month=${weekDate.getMonth()}&year=${weekDate.getFullYear()}`);
  };

  const pendingCount = pendingUsers.length;

  return (
    <OpsPanel
      fillHeight
      title="Worklogs approval"
      panelMenu={
        <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {pendingCount > 0 && (
            <Button
              size="small"
              onClick={handleApproveAll}
              disabled={isApprovingAll || isLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 11.5,
                minWidth: 0,
                color: Token.textPrimary,
                border: `1px solid ${Token.borderDefault}`,
                borderRadius: Token.radiusControl,
                padding: "2px 8px",
                lineHeight: 1.4,
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: Token.surfaceHover,
                  borderColor: Token.borderStrong,
                },
              }}
            >
              {isApprovingAll ? "Approving…" : "Approve all"}
            </Button>
          )}
          <Chip
            label={`${pendingCount} pending`}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: pendingCount > 0 ? Token.statusWarningBg : Token.surfaceActive,
              color: pendingCount > 0 ? Token.statusWarningFg : Token.textTertiary,
              borderRadius: Token.radiusChip,
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </Box>
      }
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 180,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {isLoading ? (
          <WorklogsApprovalSkeleton />
        ) : pendingUsers.length === 0 ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              gap: 6,
              padding: "16px 8px",
              textAlign: "center",
            }}
          >
            <PlaylistAddCheckOutlinedIcon sx={{ fontSize: 32, color: Token.textTertiary }} />
            <Box style={{ fontSize: 13, fontWeight: 600, color: Token.textPrimary }}>You're all caught up</Box>
            <Box style={{ fontSize: 12, color: Token.textTertiary, lineHeight: 1.4 }}>No worklogs waiting for approval</Box>
          </Box>
        ) : (
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {pendingUsers.map((user, index) => {
              const uniqueId = worklogApprovalKey(user.assignationExternalId, user.weekFirstDayIso);
              const isProcessing = processingId === uniqueId || isApprovingAll;

              return (
                <Box
                  key={uniqueId}
                  onClick={() => openUserWorklog(user)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 4px",
                    gap: 10,
                    cursor: "pointer",
                    borderRadius: Token.radiusControl,
                    borderBottom: index < pendingUsers.length - 1 ? `1px solid ${Token.borderSubtle}` : "none",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: Token.surfaceHover,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: Token.surfaceActive,
                      color: Token.textSecondary,
                    }}
                  >
                    {OpsStringUtils.getInitials(user.userFullName)}
                  </Avatar>

                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Box
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: Token.textPrimary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user.userFullName}
                    </Box>
                    <Box
                      style={{
                        fontSize: 11,
                        color: Token.textTertiary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Box
                        component="span"
                        onClick={(event: React.MouseEvent) => openProjectReport(event, user)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            color: Token.textLink,
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {user.projectName}
                      </Box>{" "}
                      · {user.weekLabel}
                    </Box>
                  </Box>

                  <Box
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: Token.textSecondary,
                      fontFamily: "var(--font-mono)",
                      marginRight: 4,
                    }}
                  >
                    {user.totalHours} h
                  </Box>

                  <Box style={{ display: "flex", gap: 4 }} onClick={(event) => event.stopPropagation()}>
                    <Tooltip title="Approve">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleApprove(user)}
                          disabled={isProcessing}
                          sx={{
                            ...iconButtonBase,
                            backgroundColor: Token.statusSuccessBg,
                            color: Token.statusSuccessFg,
                            "&:hover": {
                              backgroundColor: Token.statusSuccessFg,
                              color: Token.textOnAction,
                            },
                          }}
                        >
                          {processingId === uniqueId ? (
                            <CircularProgress size={14} sx={{ color: "inherit" }} />
                          ) : (
                            <CheckIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Send back">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleReject(user)}
                          disabled={isProcessing}
                          sx={{
                            ...iconButtonBase,
                            backgroundColor: Token.surfaceRaised,
                            color: Token.textTertiary,
                            border: `1px solid ${Token.borderDefault}`,
                            "&:hover": {
                              backgroundColor: Token.surfaceHover,
                              color: Token.textSecondary,
                            },
                          }}
                        >
                          <UndoIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Box
          onClick={() => history.push(Routes.WORKLOG_REPORT)}
          style={{
            marginTop: "auto",
            paddingTop: 12,
            fontSize: 12,
            color: Token.textLink,
            cursor: "pointer",
            fontWeight: 500,
          }}
          sx={{
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          Open approvals queue
        </Box>
      </Box>
    </OpsPanel>
  );
};
